<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { ArrowLeft, Bot, Check, Circle, Code2, RefreshCw, RotateCcw, ScrollText, X } from '@lucide/vue'
import { Badge, Button, Drawer, PageHeader, Section, Spinner } from '@drama/ui'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { getCreatorProject, getCreatorProjectAsset, retryCreatorProject } from '../api/creator-projects.js'
import { creatorPlaytestReport } from '../lib/index.js'

const NODE_DEFS = [
  { key: 'input', step_types: [], executor: 'script' },
  { key: 'story', step_types: ['design'], executor: 'agent' },
  { key: 'level', step_types: ['design'], executor: 'agent' },
  { key: 'assets', step_types: ['asset_plan', 'asset_production'], executor: 'agent + script' },
  { key: 'json', step_types: ['structure'], executor: 'agent + script' },
  { key: 'voice', step_types: ['voice_select'], executor: 'agent + script' },
  { key: 'detect', step_types: ['validate'], executor: 'script' },
  { key: 'assemble', step_types: ['assemble'], executor: 'script' },
  { key: 'inspect', step_types: ['inspect'], executor: 'agent' },
  { key: 'play', step_types: ['playtest'], executor: 'agent + script' },
  { key: 'done', step_types: ['localize'], executor: 'script' },
]
const MAX_AUTOMATIC_ATTEMPTS = 3
const props = defineProps({ id: { type: String, required: true } })
const { locale, t } = useI18n()
const router = useRouter()
const detail = ref(null)
const loading = ref(false)
const error = ref('')
const selectedNode = ref(null)
const selectedAsset = ref(null)
const assetPreviews = ref({})
const assetPreviewErrors = ref({})
const retryingNode = ref(false)

const selectedRestartStep = computed(() => {
  if (selectedNode.value === null || selectedNode.value.latest === null) return null
  const step = selectedNode.value.latest.step_type
  if (step === 'assemble') return 'structure'
  return ['asset_production', 'inspect', 'playtest'].includes(step) ? step : null
})

const retriesFromStructure = computed(() => selectedRestartStep.value === 'structure')

const canRetrySelectedNode = computed(() => {
  if (selectedNode.value === null || selectedNode.value.isFallback === true) return false
  const latest = selectedNode.value.latest
  return detail.value.project.status === 'generation_failed'
    && detail.value.task.status === 'failed'
    && latest !== null
    && latest.status === 'failed'
    && (latest.step_type === detail.value.task.current_step || selectedRestartStep.value !== null)
})

const nodes = computed(() => NODE_DEFS.map(definition => {
  const attempts = definition.key === 'input'
    ? [inputAttempt()]
    : definition.key === 'done'
      ? [completionAttempt()]
      : detail.value.steps
          .filter(step => definition.step_types.includes(step.step_type))
          .sort((left, right) => new Date(left.started_at) - new Date(right.started_at))
  const latest = attempts.at(-1) || null
  return { ...definition, attempts, latest, status: latest === null ? 'pending' : latest.status }
}))

const fallbacks = computed(() => [
  fallbackRecord('validate', 'detect'),
  fallbackRecord('inspect', 'inspect'),
  fallbackRecord('playtest', 'play'),
])

function fallbackRecord(stepType, sourceNode) {
  const attempts = detail.value.steps
    .filter(step => step.step_type === stepType && step.status === 'failed' && step.attempt_no < MAX_AUTOMATIC_ATTEMPTS)
    .sort((left, right) => new Date(left.started_at) - new Date(right.started_at))
  return { key: stepType, sourceNode, attempts, isFallback: true }
}

function fallback(key) {
  return fallbacks.value.find(item => item.key === key)
}

function inputAttempt() {
  return {
    id: 'project-input', attempt_no: 1, status: 'completed', executor_type: 'script',
    started_at: detail.value.project.created_at, completed_at: detail.value.project.created_at,
    input: { idea_text: detail.value.project.idea_text, language: detail.value.project.language },
    output: { project_id: detail.value.project.id, task_id: detail.value.task.id }, error_message: null,
  }
}

function completionAttempt() {
  return {
    id: 'project-completion', attempt_no: 1,
    status: detail.value.task.status === 'queued' ? 'pending' : detail.value.task.status,
    executor_type: 'script', started_at: detail.value.task.completed_at, completed_at: detail.value.task.completed_at,
    input: { candidate_pack_id: detail.value.project.current_candidate_pack_id, candidate_version_id: detail.value.project.current_candidate_version_id },
    output: { task_status: detail.value.task.status, project_status: detail.value.project.status },
    error_message: detail.value.task.error_message,
  }
}

onMounted(load)
onBeforeUnmount(revokeAssetPreviews)

async function load() {
  loading.value = true
  error.value = ''
  try {
    detail.value = await getCreatorProject(props.id)
    void loadAssetPreviews()
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    loading.value = false
  }
}

async function retrySelectedNode() {
  retryingNode.value = true
  error.value = ''
  try {
    await retryCreatorProject(props.id, selectedRestartStep.value)
    selectedNode.value = null
    await load()
  } catch (requestError) {
    error.value = requestError.message
  } finally {
    retryingNode.value = false
  }
}

async function loadAssetPreviews() {
  revokeAssetPreviews()
  assetPreviewErrors.value = {}
  await Promise.all(detail.value.assets.map(async asset => {
    try {
      const blob = await getCreatorProjectAsset(props.id, asset.asset_index)
      assetPreviews.value = { ...assetPreviews.value, [asset.asset_index]: URL.createObjectURL(blob) }
    } catch (assetError) {
      assetPreviewErrors.value = { ...assetPreviewErrors.value, [asset.asset_index]: assetError.message }
    }
  }))
}

function revokeAssetPreviews() {
  for (const preview of Object.values(assetPreviews.value)) URL.revokeObjectURL(preview)
  assetPreviews.value = {}
}

function statusVariant(status) {
  switch (status) {
    case 'completed': return 'success'
    case 'running': return 'info'
    case 'failed': return 'danger'
    case 'pending': return 'neutral'
    default: throw new Error('Unhandled creator log node status: ' + status)
  }
}

function projectStatusVariant(status) {
  switch (status) {
    case 'pending_review': return 'warning'
    case 'published': return 'success'
    case 'generation_failed':
    case 'review_rejected': return 'danger'
    case 'generating': return 'info'
    case 'archived': return 'neutral'
    default: throw new Error('Unhandled creator project status: ' + status)
  }
}

function formatDate(value) {
  if (value === null) return '—'
  return new Intl.DateTimeFormat(locale.value, {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).format(new Date(value))
}

function duration(step) {
  if (step.started_at === null || step.completed_at === null) return '—'
  const milliseconds = new Date(step.completed_at).getTime() - new Date(step.started_at).getTime()
  return milliseconds < 1000 ? milliseconds + ' ms' : (milliseconds / 1000).toFixed(1) + ' s'
}

function totalDuration(attempts) {
  const milliseconds = attempts.reduce((total, step) => {
    if (step.started_at === null || step.completed_at === null) return total
    return total + new Date(step.completed_at).getTime() - new Date(step.started_at).getTime()
  }, 0)
  if (milliseconds < 1000) return milliseconds + ' ms'
  if (milliseconds < 60000) return (milliseconds / 1000).toFixed(1) + ' s'
  return (milliseconds / 60000).toFixed(1) + ' min'
}

function nodeRunSummary(node) {
  if (node.key === 'assets') {
    const planning = node.attempts.filter(step => step.step_type === 'asset_plan').length
    const production = node.attempts.filter(step => step.step_type === 'asset_production').length
    return t('visualGameAdmin.ugc.logPage.assetRuns', { planning, production, duration: totalDuration(node.attempts) })
  }
  return t('visualGameAdmin.ugc.logPage.runSummary', { count: node.attempts.length, duration: totalDuration(node.attempts) })
}

function drawerTitle() {
  if (selectedNode.value.isFallback) return t('visualGameAdmin.ugc.fallback.' + selectedNode.value.key) + ' · ' + t('visualGameAdmin.ugc.logPage.failureDetail')
  return t('visualGameAdmin.ugc.flow.' + selectedNode.value.key) + ' · ' + t('visualGameAdmin.ugc.logPage.nodeDetail')
}

function formatted(value) {
  return value === null ? t('visualGameAdmin.ugc.logPage.noData') : JSON.stringify(value, null, 2)
}

function playtestCoverage(step) {
  const endings = creatorPlaytestReport(step).endings
  const triggered = endings.filter(ending => ending.triggered === true).length
  return Math.round((triggered / endings.length) * 100)
}
</script>

<template>
  <div class="ugc-logs vga-page" data-insp-path="apps/visual-game-admin/src/pages/CreatorProjectLogs.vue">
    <PageHeader :title="t('visualGameAdmin.ugc.logPage.title')">
      <template #actions>
        <Button variant="secondary" size="sm" @click="router.push('/creator-projects')">
          <ArrowLeft :size="14" /> {{ t('visualGameAdmin.ugc.logPage.back') }}
        </Button>
        <Button variant="secondary" size="sm" :loading="loading" @click="load">
          <RefreshCw :size="14" /> {{ t('visualGameAdmin.ugc.refresh') }}
        </Button>
      </template>
    </PageHeader>
    <p class="ugc-logs__subtitle">{{ t('visualGameAdmin.ugc.logPage.subtitle') }}</p>

    <div v-if="loading" class="vga-state"><Spinner /><span>{{ t('visualGameAdmin.ugc.loading') }}</span></div>
    <div v-else-if="error" class="vga-state">
      <p class="vga-error">{{ error }}</p>
      <Button variant="secondary" @click="load">{{ t('visualGameAdmin.common.retry') }}</Button>
    </div>
    <template v-else-if="detail">
      <Section>
        <div class="ugc-logs__project">
          <div><span>{{ t('visualGameAdmin.ugc.detail') }}</span><strong>{{ detail.project.title }}</strong></div>
          <Badge :variant="projectStatusVariant(detail.project.status)">{{ t('visualGameAdmin.ugc.status.' + detail.project.status) }}</Badge>
        </div>
      </Section>

      <Section :title="t('visualGameAdmin.ugc.logs')">
        <div class="ugc-logs__flow">
          <div class="ugc-logs__canvas">
            <svg class="ugc-logs__edges" viewBox="0 0 1670 640" aria-hidden="true">
              <defs><marker id="ugc-arrow" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto"><path d="M0 0 L8 4 L0 8 Z" /></marker></defs>
              <path d="M150 320 H190 M340 320 H380" />
              <path d="M530 320 H550 V90 H570 M530 320 H570 M530 320 H550 V550 H570" />
              <path d="M720 90 H740 V320 H760 M720 320 H760 M720 550 H740 V320 H760" />
              <path d="M910 320 H950 M1100 320 H1140 M1290 320 H1330 M1480 320 H1520" />
              <path :class="['is-fallback', { 'has-failures': fallback('validate').attempts.length > 0 }]" d="M835 410 V440 H645 V410" />
              <path :class="['is-fallback', { 'has-failures': fallback('inspect').attempts.length > 0 }]" d="M1215 230 V190 H645 V230" />
              <path :class="['is-fallback', { 'has-failures': fallback('playtest').attempts.length > 0 }]" d="M1405 230 V210 H645 V230" />
            </svg>
            <button v-for="item in fallbacks" v-show="item.attempts.length > 0" :key="'fallback-' + item.key" type="button" :class="['ugc-logs__fallback', 'fallback-' + item.key]" @click="selectedNode = item">
              <RotateCcw :size="13" />
              {{ t('visualGameAdmin.ugc.logPage.fallbackCount', { count: item.attempts.length }) }}
            </button>
            <button v-for="node in nodes" :key="node.key" class="ugc-logs__node" type="button" :class="['node-' + node.key, 'is-' + node.status]" @click="selectedNode = node">
              <span class="ugc-logs__icon">
                <Check v-if="node.status === 'completed'" :size="18" />
                <X v-else-if="node.status === 'failed'" :size="18" />
                <Bot v-else-if="node.status === 'running'" :size="18" />
                <Circle v-else :size="18" />
              </span>
              <strong>{{ t('visualGameAdmin.ugc.flow.' + node.key) }}</strong>
              <small>{{ t('visualGameAdmin.ugc.flowRole.' + node.key) }}</small>
              <Badge v-if="node.latest" :variant="statusVariant(node.status)" size="sm">{{ t('visualGameAdmin.ugc.runStatus.' + node.status) }}</Badge>
              <small v-else>{{ t('visualGameAdmin.ugc.logPage.nodePending') }}</small>
              <small v-if="node.attempts.length" class="ugc-logs__metrics">{{ nodeRunSummary(node) }}</small>
            </button>
          </div>
        </div>
      </Section>
    </template>

    <Drawer
      :open="selectedNode !== null"
      :title="selectedNode ? drawerTitle() : ''"
      width="min(760px, 96vw)"
      @update:open="selectedNode = $event ? selectedNode : null"
    >
      <div v-if="selectedNode" class="ugc-logs__attempts">
        <section v-if="canRetrySelectedNode" class="ugc-logs__retry-node">
          <div>
            <strong>{{ t(retriesFromStructure ? 'visualGameAdmin.ugc.logPage.retryStructure' : 'visualGameAdmin.ugc.logPage.retryNode') }}</strong>
            <p>{{ t(retriesFromStructure ? 'visualGameAdmin.ugc.logPage.retryStructureNotice' : 'visualGameAdmin.ugc.logPage.retryNodeNotice') }}</p>
          </div>
          <Button :loading="retryingNode" @click="retrySelectedNode">
            <RotateCcw :size="15" /> {{ t(retriesFromStructure ? 'visualGameAdmin.ugc.logPage.retryStructureAction' : 'visualGameAdmin.ugc.logPage.retryNodeAction') }}
          </Button>
        </section>
        <section v-if="selectedNode.key === 'assets'" class="ugc-logs__asset-section">
          <header class="ugc-logs__asset-header">
            <div>
              <h2>{{ t('visualGameAdmin.ugc.logPage.producedAssets') }}</h2>
              <p>{{ t('visualGameAdmin.ugc.logPage.producedAssetsSummary', { count: detail.assets.length }) }}</p>
            </div>
          </header>
          <div v-if="detail.assets.length" class="ugc-logs__asset-grid">
            <article v-for="asset in detail.assets" :key="asset.sha256" class="ugc-logs__asset-card">
              <button v-if="assetPreviews[asset.asset_index]" type="button" class="ugc-logs__asset-preview" @click="selectedAsset = asset">
                <img v-if="asset.media_type === 'image'" :src="assetPreviews[asset.asset_index]" :alt="asset.state_id" loading="lazy">
                <video v-else :src="assetPreviews[asset.asset_index]" controls preload="metadata" />
              </button>
              <div v-else class="ugc-logs__asset-preview ugc-logs__asset-preview-state">{{ assetPreviewErrors[asset.asset_index] || t('visualGameAdmin.ugc.loading') }}</div>
              <div class="ugc-logs__asset-body">
                <div class="ugc-logs__asset-title">
                  <strong>{{ asset.state_id }}</strong>
                  <Badge :variant="asset.accepted ? 'success' : 'danger'" size="sm">{{ asset.accepted ? t('visualGameAdmin.ugc.logPage.assetAccepted') : t('visualGameAdmin.ugc.logPage.assetRejected') }}</Badge>
                </div>
                <small>{{ asset.tag }}</small>
                <dl>
                  <div><dt>{{ t('visualGameAdmin.ugc.logPage.assetType') }}</dt><dd>{{ asset.media_type }}</dd></div>
                  <div><dt>{{ t('visualGameAdmin.ugc.logPage.assetSize') }}</dt><dd>{{ asset.width }} × {{ asset.height }}</dd></div>
                </dl>
                <p v-for="observation in asset.observations" :key="observation">{{ observation }}</p>
                <code :title="asset.sha256">SHA-256 · {{ asset.sha256.slice(0, 12) }}</code>
              </div>
            </article>
          </div>
          <div v-else class="ugc-logs__empty"><ScrollText :size="24" /> {{ t('visualGameAdmin.ugc.logPage.noProducedAssets') }}</div>
        </section>
        <div v-if="selectedNode.attempts.length === 0" class="ugc-logs__empty">
          <ScrollText :size="24" /> {{ t('visualGameAdmin.ugc.logPage.nodePending') }}
        </div>
        <article v-for="step in [...selectedNode.attempts].reverse()" :key="step.id" class="ugc-logs__attempt">
          <header>
            <strong>{{ selectedNode.isFallback ? t('visualGameAdmin.ugc.logPage.failureAttempt', { number: step.attempt_no }) : t('visualGameAdmin.ugc.logPage.attempt', { number: step.attempt_no }) }}</strong>
            <Badge :variant="statusVariant(step.status)" size="sm">{{ step.status }}</Badge>
          </header>
          <dl>
            <div><dt>{{ t('visualGameAdmin.ugc.logPage.sourceStep') }}</dt><dd>{{ t('visualGameAdmin.ugc.sourceStep.' + (step.step_type || selectedNode.key)) }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.ugc.logPage.executor') }}</dt><dd>{{ step.executor_type }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.ugc.logPage.startedAt') }}</dt><dd>{{ formatDate(step.started_at) }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.ugc.logPage.completedAt') }}</dt><dd>{{ formatDate(step.completed_at) }}</dd></div>
            <div><dt>{{ t('visualGameAdmin.ugc.logPage.duration') }}</dt><dd>{{ duration(step) }}</dd></div>
          </dl>
          <section v-if="step.error_message" class="ugc-logs__error">
            <h3>{{ t('visualGameAdmin.ugc.logPage.error') }}</h3><pre>{{ step.error_message }}</pre>
          </section>
          <section v-if="step.step_type === 'playtest' && creatorPlaytestReport(step) !== null" class="ugc-logs__playtest-report">
            <header>
              <div>
                <h3>{{ t('visualGameAdmin.ugc.logPage.playtestReport') }}</h3>
                <p>{{ creatorPlaytestReport(step).candidate_version_id }}</p>
              </div>
              <Badge :variant="creatorPlaytestReport(step).passed ? 'success' : 'danger'">
                {{ creatorPlaytestReport(step).passed ? t('visualGameAdmin.ugc.logPage.playtestPassed') : t('visualGameAdmin.ugc.logPage.playtestFailed') }}
              </Badge>
            </header>
            <dl class="ugc-logs__playtest-metrics">
              <div><dt>{{ t('visualGameAdmin.ugc.logPage.endingCoverage') }}</dt><dd>{{ playtestCoverage(step) }}%</dd></div>
              <div><dt>{{ t('visualGameAdmin.ugc.logPage.endingsTriggered') }}</dt><dd>{{ creatorPlaytestReport(step).endings.filter(ending => ending.triggered).length }} / {{ creatorPlaytestReport(step).endings.length }}</dd></div>
              <div><dt>{{ t('visualGameAdmin.ugc.logPage.reportDuration') }}</dt><dd>{{ duration(step) }}</dd></div>
            </dl>
            <div class="ugc-logs__ending-results">
              <article v-for="ending in creatorPlaytestReport(step).endings" :key="ending.ending_id" :class="{ 'is-passed': ending.triggered }">
                <Check v-if="ending.triggered" :size="16" />
                <X v-else :size="16" />
                <div><strong>{{ ending.ending_id }}</strong><small>{{ ending.triggered ? t('visualGameAdmin.ugc.logPage.endingReached') : t('visualGameAdmin.ugc.logPage.endingUnreached') }}</small></div>
              </article>
            </div>
          </section>
          <section><h3><Code2 :size="15" /> {{ t('visualGameAdmin.ugc.logPage.input') }}</h3><pre>{{ formatted(step.input) }}</pre></section>
          <section><h3><Code2 :size="15" /> {{ t('visualGameAdmin.ugc.logPage.output') }}</h3><pre>{{ formatted(step.output) }}</pre></section>
        </article>
      </div>
    </Drawer>

    <div v-if="selectedAsset" class="ugc-logs__lightbox" role="dialog" aria-modal="true" :aria-label="selectedAsset.state_id" @click.self="selectedAsset = null">
      <button type="button" class="ugc-logs__lightbox-close" :aria-label="t('visualGameAdmin.common.close')" @click="selectedAsset = null"><X :size="22" /></button>
      <img v-if="selectedAsset.media_type === 'image'" :src="assetPreviews[selectedAsset.asset_index]" :alt="selectedAsset.state_id">
      <video v-else :src="assetPreviews[selectedAsset.asset_index]" controls autoplay />
      <strong>{{ selectedAsset.state_id }}</strong>
    </div>
  </div>
</template>

<style scoped>
.ugc-logs__subtitle { margin: calc(var(--drama-space-lg) * -1) 0 0; color: var(--drama-color-text-muted); }
.ugc-logs__project { display: flex; align-items: center; justify-content: space-between; gap: var(--drama-space-lg); }
.ugc-logs__project > div { display: grid; gap: var(--drama-space-xs); min-width: 0; }
.ugc-logs__project span { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.ugc-logs__project strong { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.ugc-logs__flow { overflow-x: auto; padding-bottom: var(--drama-space-sm); }
.ugc-logs__canvas { position: relative; width: 1670px; height: 640px; }
.ugc-logs__edges { position: absolute; inset: 0; width: 1670px; height: 640px; pointer-events: none; }
.ugc-logs__edges path { fill: none; stroke: var(--drama-color-text-muted); stroke-width: 2; marker-end: url(#ugc-arrow); }
.ugc-logs__edges marker path { fill: var(--drama-color-text-muted); stroke: none; }
.ugc-logs__edges path.is-fallback { stroke: var(--drama-color-border); stroke-dasharray: 7 6; }
.ugc-logs__edges path.is-fallback.has-failures { stroke: var(--drama-color-danger); }
.ugc-logs__fallback { position: absolute; z-index: 2; display: flex; align-items: center; gap: var(--drama-space-xs); padding: var(--drama-space-xs) var(--drama-space-sm); border: 1px solid var(--drama-color-danger); border-radius: var(--drama-radius-full); background: var(--drama-color-surface); color: var(--drama-color-danger); font-size: var(--drama-font-size-xs); cursor: pointer; }
.ugc-logs__fallback:hover { box-shadow: var(--drama-shadow-sm); }
.ugc-logs__fallback.fallback-validate { left: 690px; top: 422px; }
.ugc-logs__fallback.fallback-inspect { left: 930px; top: 176px; }
.ugc-logs__fallback.fallback-playtest { left: 1160px; top: 196px; }
.ugc-logs__node { position: absolute; box-sizing: border-box; width: 150px; height: 180px; display: grid; place-items: center; align-content: center; gap: var(--drama-space-xs); padding: var(--drama-space-md); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-lg); background: var(--drama-color-surface-alt); color: var(--drama-color-text); text-align: center; cursor: pointer; }
.ugc-logs__node.node-input { left: 0; top: 230px; }.ugc-logs__node.node-story { left: 190px; top: 230px; }.ugc-logs__node.node-level { left: 380px; top: 230px; }
.ugc-logs__node.node-assets { left: 570px; top: 0; }.ugc-logs__node.node-json { left: 570px; top: 230px; }.ugc-logs__node.node-voice { left: 570px; top: 460px; }
.ugc-logs__node.node-detect { left: 760px; top: 230px; }.ugc-logs__node.node-assemble { left: 950px; top: 230px; }.ugc-logs__node.node-inspect { left: 1140px; top: 230px; }
.ugc-logs__node.node-play { left: 1330px; top: 230px; }.ugc-logs__node.node-done { left: 1520px; top: 230px; }
.ugc-logs__node:hover { border-color: var(--drama-color-primary); box-shadow: var(--drama-shadow-sm); }
.ugc-logs__node.is-completed .ugc-logs__icon { color: var(--drama-color-success); }
.ugc-logs__node.is-running .ugc-logs__icon { color: var(--drama-color-info); }
.ugc-logs__node.is-failed .ugc-logs__icon { color: var(--drama-color-danger); }
.ugc-logs__node small { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.ugc-logs__node .ugc-logs__metrics { color: var(--drama-color-text); font-family: var(--drama-font-family-mono); }
.ugc-logs__icon { display: grid; place-items: center; width: 36px; height: 36px; border-radius: var(--drama-radius-full); background: var(--drama-color-surface); }
.ugc-logs__attempts { display: grid; gap: var(--drama-space-lg); }
.ugc-logs__retry-node { display: flex; align-items: center; justify-content: space-between; gap: var(--drama-space-lg); padding: var(--drama-space-md); border: 1px solid var(--drama-color-danger); border-radius: var(--drama-radius-lg); background: var(--drama-color-surface-alt); }
.ugc-logs__retry-node div { display: grid; gap: var(--drama-space-xs); }
.ugc-logs__retry-node p { margin: 0; color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
.ugc-logs__asset-section { display: grid; gap: var(--drama-space-md); }
.ugc-logs__asset-header h2 { margin: 0; font-size: var(--drama-font-size-lg); }
.ugc-logs__asset-header p { margin: var(--drama-space-xs) 0 0; color: var(--drama-color-text-muted); font-size: var(--drama-font-size-sm); }
.ugc-logs__asset-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--drama-space-md); }
.ugc-logs__asset-card { overflow: hidden; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-lg); background: var(--drama-color-surface); }
.ugc-logs__asset-preview { display: block; width: 100%; aspect-ratio: 16 / 9; overflow: hidden; padding: 0; border: 0; background: var(--drama-color-surface-alt); cursor: zoom-in; }
.ugc-logs__asset-preview img, .ugc-logs__asset-preview video { display: block; width: 100%; height: 100%; object-fit: cover; }
.ugc-logs__asset-preview-state { display: grid; place-items: center; padding: var(--drama-space-md); color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); text-align: center; }
.ugc-logs__asset-body { display: grid; gap: var(--drama-space-sm); padding: var(--drama-space-md); }
.ugc-logs__asset-title { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--drama-space-sm); }
.ugc-logs__asset-title strong { overflow-wrap: anywhere; }
.ugc-logs__asset-body > small { color: var(--drama-color-text-muted); overflow-wrap: anywhere; }
.ugc-logs__asset-body dl { display: flex; gap: var(--drama-space-lg); margin: 0; }
.ugc-logs__asset-body dl div { display: grid; gap: 2px; }
.ugc-logs__asset-body dt { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.ugc-logs__asset-body dd { margin: 0; font-size: var(--drama-font-size-sm); }
.ugc-logs__asset-body p { margin: 0; color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); line-height: var(--drama-line-height-relaxed); }
.ugc-logs__asset-body code { overflow: hidden; color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); text-overflow: ellipsis; white-space: nowrap; }
.ugc-logs__lightbox { position: fixed; z-index: 1000; inset: 0; display: grid; place-items: center; align-content: center; gap: var(--drama-space-md); padding: var(--drama-space-xl); background: color-mix(in srgb, var(--drama-color-text) 88%, transparent); color: var(--drama-color-surface); }
.ugc-logs__lightbox img, .ugc-logs__lightbox video { max-width: min(1200px, 92vw); max-height: 82vh; border-radius: var(--drama-radius-lg); box-shadow: var(--drama-shadow-lg); object-fit: contain; }
.ugc-logs__lightbox-close { position: fixed; top: var(--drama-space-xl); right: var(--drama-space-xl); display: grid; place-items: center; width: 42px; height: 42px; border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-full); background: var(--drama-color-surface); color: var(--drama-color-text); cursor: pointer; }
.ugc-logs__attempt { display: grid; gap: var(--drama-space-lg); padding: var(--drama-space-lg); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-lg); }
.ugc-logs__attempt > header { display: flex; align-items: center; justify-content: space-between; gap: var(--drama-space-md); }
.ugc-logs__attempt dl { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--drama-space-md); margin: 0; }
.ugc-logs__attempt dl div { display: grid; gap: var(--drama-space-xs); }
.ugc-logs__attempt dt { color: var(--drama-color-text-muted); font-size: var(--drama-font-size-xs); }
.ugc-logs__attempt dd { margin: 0; font-size: var(--drama-font-size-sm); }
.ugc-logs__attempt h3 { display: flex; align-items: center; gap: var(--drama-space-xs); margin: 0 0 var(--drama-space-sm); font-size: var(--drama-font-size-sm); }
.ugc-logs__attempt pre { max-height: 360px; overflow: auto; margin: 0; padding: var(--drama-space-md); border-radius: var(--drama-radius-md); background: var(--drama-color-surface-alt); color: var(--drama-color-text); font-family: var(--drama-font-family-mono); font-size: var(--drama-font-size-xs); line-height: var(--drama-line-height-relaxed); white-space: pre-wrap; word-break: break-word; }
.ugc-logs__error h3 { color: var(--drama-color-danger); }
.ugc-logs__playtest-report { display: grid; gap: var(--drama-space-md); padding: var(--drama-space-lg); border: 1px solid var(--drama-color-border); border-radius: var(--drama-radius-lg); background: var(--drama-color-surface-alt); }
.ugc-logs__playtest-report > header { display: flex; align-items: flex-start; justify-content: space-between; gap: var(--drama-space-md); }
.ugc-logs__playtest-report h3 { margin: 0; font-size: var(--drama-font-size-lg); }
.ugc-logs__playtest-report header p { margin: var(--drama-space-xs) 0 0; color: var(--drama-color-text-muted); font-family: var(--drama-font-family-mono); font-size: var(--drama-font-size-xs); }
.ugc-logs__playtest-metrics { display: grid !important; grid-template-columns: repeat(3, minmax(0, 1fr)) !important; }
.ugc-logs__playtest-metrics dd { font-size: var(--drama-font-size-lg) !important; font-weight: 700; }
.ugc-logs__ending-results { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--drama-space-sm); }
.ugc-logs__ending-results article { display: flex; align-items: center; gap: var(--drama-space-sm); padding: var(--drama-space-sm) var(--drama-space-md); border: 1px solid var(--drama-color-danger); border-radius: var(--drama-radius-md); color: var(--drama-color-danger); background: var(--drama-color-surface); }
.ugc-logs__ending-results article.is-passed { border-color: var(--drama-color-success); color: var(--drama-color-success); }
.ugc-logs__ending-results article div { display: grid; gap: 2px; min-width: 0; }
.ugc-logs__ending-results strong { overflow: hidden; color: var(--drama-color-text); font-family: var(--drama-font-family-mono); font-size: var(--drama-font-size-xs); text-overflow: ellipsis; white-space: nowrap; }
.ugc-logs__ending-results small { font-size: var(--drama-font-size-xs); }
.ugc-logs__empty { display: flex; align-items: center; justify-content: center; gap: var(--drama-space-sm); min-height: 160px; color: var(--drama-color-text-muted); }
@media (max-width: 720px) { .ugc-logs__attempt dl, .ugc-logs__asset-grid, .ugc-logs__playtest-metrics, .ugc-logs__ending-results { grid-template-columns: 1fr !important; } .ugc-logs__retry-node { align-items: stretch; flex-direction: column; } }
</style>
